import Modal from '~/components/Modal';
import useStackablePortal, { isTopOfStack } from '~/hooks/useStackablePortal';
import * as React from 'react';
import { Popup as SemanticPopup, PopupProps } from 'semantic-ui-react';
import { zIndexValues } from '~/styles/constants';

type Props = {
  // if the div wrapping the trigger needs additional classes (such as flex, w-full)
  wrapperClassName?: string;
  // Style object passed to the popup trigger wrapper
  wrapperStyle?: React.CSSProperties;
  // Overrides popper's z-index (zIndexValues.overlay) when provided
  zIndex?: number;
};

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

const Popup = (props: PopupProps & Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updatedProps: any = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divProps: any = {};
  const portalKey = useStackablePortal(!!props.open);

  const onClose =
    props.on === 'click' && !props.closeOnDocumentClick
      ? // Prevents click triggered Popups from calling onClose when not top most stacked component
        (evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
          if (isTopOfStack(portalKey)) {
            if (props.onClose) {
              props.onClose(evt, props);
            }
          }
        }
      : props.onClose;

  if (props.wrapperClassName) {
    divProps.className = props.wrapperClassName;
  }
  if (props.wrapperStyle) {
    divProps.style = props.wrapperStyle;
  }
  if (props.trigger) {
    // wrap trigger element in a div -- some elements (e.g. button) don't handle positioning, mouseover, etc.
    // correctly otherwise
    updatedProps.trigger = props.trigger ? <div {...divProps}>{props.trigger}</div> : undefined;
  }

  const modalZIndex = React.useContext(Modal.ZIndexContext);
  const borderRadius = props.style?.borderRadius ? props.style.borderRadius : '8px';

  return (
    <SemanticPopup
      {...props}
      {...updatedProps}
      onClose={onClose}
      flowing
      style={{ ...props.style, borderRadius: borderRadius }}
      // Provide props to the top most wrapper div element
      popper={{
        style: {
          zIndex: props.zIndex || (modalZIndex && modalZIndex + 1) || zIndexValues.overlay,
        },
      }}
    >
      {(props.on === 'hover' && <PopupContent>{props.children}</PopupContent>) || props.children}
    </SemanticPopup>
  );
};

interface ComponentProps {
  children: React.ReactNode;
}

const PopupContent = ({ children }: ComponentProps) => (
  // Disabling these rules on this context is fine because the div is only used to stop pointer events propagation
  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
  <div onClick={stopPropagation}>{children}</div>
);

export { Popup, PopupContent };

export default Popup;
